import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const LeaseContractGenerator = ({ accountId, accountName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatCurrency = (v) => {
    if (!v && v !== 0) return '—';
    return `KES ${Number(v)?.toLocaleString('en-KE', { minimumFractionDigits: 0 })}`;
  };

  const buildContractHTML = (data) => {
    const { company, request, leases, vehicles, pricing, contract_ref, generated_at } = data;
    const quoteDetails = request?.quote_details || {};
    const requirements = request?.requirements || {};

    const vehicleRows = (vehicles || [])?.map((v, i) => `
      <tr style="border-bottom:1px solid #e5e7eb;">
        <td style="padding:10px 12px;">${i + 1}</td>
        <td style="padding:10px 12px;">${v?.year || ''} ${v?.make || ''} ${v?.model || ''}</td>
        <td style="padding:10px 12px;">${v?.vehicle_type || '—'}</td>
        <td style="padding:10px 12px;">${v?.plate_number || '—'}</td>
        <td style="padding:10px 12px;">${v?.color || '—'}</td>
        <td style="padding:10px 12px;">${formatDate(v?.assigned_at)}</td>
      </tr>
    `)?.join('');

    const leaseRows = (leases || [])?.map((l, i) => `
      <tr style="border-bottom:1px solid #e5e7eb;">
        <td style="padding:10px 12px;">${l?.lease_number || '—'}</td>
        <td style="padding:10px 12px; text-transform:capitalize;">${l?.lease_status || '—'}</td>
        <td style="padding:10px 12px;">${formatDate(l?.start_at)}</td>
        <td style="padding:10px 12px;">${formatDate(l?.end_at)}</td>
        <td style="padding:10px 12px; font-weight:600;">${formatCurrency(l?.monthly_price)}</td>
        <td style="padding:10px 12px;">${l?.deposit_terms || '—'}</td>
      </tr>
    `)?.join('');

    const termsItems = [
      quoteDetails?.slaTerms && `<li><strong>SLA Terms:</strong> ${quoteDetails?.slaTerms}</li>`,
      quoteDetails?.maintenanceFee && `<li><strong>Maintenance Bundle:</strong> ${formatCurrency(quoteDetails?.maintenanceFee)}/month</li>`,
      quoteDetails?.overMileageRate && `<li><strong>Over-Mileage Rate:</strong> KES ${quoteDetails?.overMileageRate}/km</li>`,
      quoteDetails?.depositTerms && `<li><strong>Deposit Terms:</strong> ${quoteDetails?.depositTerms}</li>`,
      request?.lease_term_months && `<li><strong>Lease Duration:</strong> ${request?.lease_term_months} months</li>`,
      requirements?.vehicleTypes?.length && `<li><strong>Vehicle Types Requested:</strong> ${requirements?.vehicleTypes?.join(', ')}</li>`,
    ]?.filter(Boolean)?.join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Lease Contract — ${company?.company_name || ''}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #111827; background: #fff; font-size: 13px; line-height: 1.6; }
    .page { max-width: 900px; margin: 0 auto; padding: 48px 40px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 36px; padding-bottom: 24px; border-bottom: 3px solid #ffd700; }
    .brand { font-size: 26px; font-weight: 900; color: #0a1628; letter-spacing: -0.5px; }
    .brand span { color: #ffd700; }
    .contract-meta { text-align: right; }
    .contract-meta .ref { font-size: 18px; font-weight: 800; color: #0a1628; }
    .contract-meta .date { color: #6b7280; font-size: 12px; margin-top: 4px; }
    h2 { font-size: 15px; font-weight: 700; color: #0a1628; margin: 28px 0 12px; padding-bottom: 6px; border-bottom: 2px solid #f3f4f6; text-transform: uppercase; letter-spacing: 0.5px; }
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 8px; }
    .field { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; }
    .field label { display: block; font-size: 10px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
    .field span { font-size: 13px; font-weight: 600; color: #111827; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    thead tr { background: #0a1628; color: #fff; }
    thead th { padding: 10px 12px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
    tbody tr:nth-child(even) { background: #f9fafb; }
    .pricing-box { background: linear-gradient(135deg, #0a1628 0%, #1a3a6b 100%); color: #fff; border-radius: 12px; padding: 24px 28px; margin: 8px 0; display: flex; justify-content: space-between; align-items: center; }
    .pricing-box .label { font-size: 12px; color: rgba(255,255,255,0.6); margin-bottom: 4px; }
    .pricing-box .value { font-size: 22px; font-weight: 900; color: #ffd700; }
    .pricing-box .sub { font-size: 11px; color: rgba(255,255,255,0.5); margin-top: 2px; }
    ul.terms { padding-left: 20px; }
    ul.terms li { margin-bottom: 8px; color: #374151; }
    .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 48px; padding-top: 24px; border-top: 2px solid #e5e7eb; }
    .sig-block .line { border-bottom: 1px solid #9ca3af; margin: 40px 0 8px; }
    .sig-block .name { font-weight: 700; font-size: 13px; }
    .sig-block .role { font-size: 11px; color: #6b7280; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #9ca3af; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
<div class="page">
  <!-- Header -->
  <div class="header">
    <div>
      <div class="brand">On The <span>Go</span> Global</div>
      <div style="color:#6b7280; font-size:12px; margin-top:4px;">Corporate Fleet Leasing Division</div>
      <div style="color:#6b7280; font-size:12px;">Nairobi, Kenya · onthegoglobal.co.ke</div>
    </div>
    <div class="contract-meta">
      <div class="ref">${contract_ref}</div>
      <div class="date">Generated: ${formatDate(generated_at)}</div>
      <div style="margin-top:8px; display:inline-block; background:#ffd700; color:#0a1628; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:800; text-transform:uppercase;">
        ${company?.corp_status || 'Active'}
      </div>
    </div>
  </div>

  <h1 style="font-size:22px; font-weight:900; color:#0a1628; margin-bottom:4px;">Corporate Fleet Lease Agreement</h1>
  <p style="color:#6b7280; font-size:13px; margin-bottom:8px;">This agreement is entered into between On The Go Global and the corporate client named below.</p>

  <!-- Company Details -->
  <h2>Client / Company Details</h2>
  <div class="grid-2">
    <div class="field"><label>Company Name</label><span>${company?.company_name || '—'}</span></div>
    <div class="field"><label>Registration No.</label><span>${company?.reg_no || '—'}</span></div>
    <div class="field"><label>Contact Person</label><span>${company?.contact_person || '—'}</span></div>
    <div class="field"><label>Contact Phone</label><span>${company?.contact_phone || '—'}</span></div>
    <div class="field"><label>Billing Email</label><span>${company?.billing_email || '—'}</span></div>
    <div class="field"><label>Physical Address</label><span>${company?.address || '—'}</span></div>
    <div class="field"><label>Account Approved</label><span>${formatDate(company?.approved_at)}</span></div>
    <div class="field"><label>Member Since</label><span>${formatDate(company?.member_since)}</span></div>
  </div>

  <!-- Pricing Summary -->
  <h2>Pricing Summary</h2>
  <div class="pricing-box">
    <div>
      <div class="label">Total Monthly Lease Value</div>
      <div class="value">${formatCurrency(pricing?.total_monthly)}</div>
      <div class="sub">Across ${pricing?.active_leases_count || 0} active lease(s)</div>
    </div>
    <div style="text-align:right;">
      <div class="label">Fleet Size</div>
      <div class="value">${pricing?.vehicle_count || 0}</div>
      <div class="sub">Assigned vehicles</div>
    </div>
    <div style="text-align:right;">
      <div class="label">Total Contracts</div>
      <div class="value">${pricing?.total_leases_count || 0}</div>
      <div class="sub">All lease contracts</div>
    </div>
  </div>

  <!-- Lease Contracts -->
  <h2>Lease Contracts</h2>
  ${(leases || [])?.length === 0
    ? '<p style="color:#9ca3af; font-style:italic;">No lease contracts on record.</p>'
    : `<table>
        <thead><tr>
          <th>Lease No.</th><th>Status</th><th>Start Date</th><th>End Date</th><th>Monthly Rate</th><th>Deposit Terms</th>
        </tr></thead>
        <tbody>${leaseRows}</tbody>
      </table>`
  }

  <!-- Assigned Vehicles -->
  <h2>Assigned Fleet Vehicles</h2>
  ${(vehicles || [])?.length === 0
    ? '<p style="color:#9ca3af; font-style:italic;">No vehicles currently assigned.</p>'
    : `<table>
        <thead><tr>
          <th>#</th><th>Vehicle</th><th>Type</th><th>Plate No.</th><th>Color</th><th>Assigned</th>
        </tr></thead>
        <tbody>${vehicleRows}</tbody>
      </table>`
  }

  <!-- Terms & Conditions -->
  <h2>Terms &amp; Conditions</h2>
  ${termsItems
    ? `<ul class="terms">${termsItems}</ul>`
    : '<p style="color:#9ca3af; font-style:italic;">Standard lease terms apply as per On The Go Global corporate policy.</p>'
  }
  <ul class="terms" style="margin-top:12px;">
    <li>The lessee agrees to use all assigned vehicles solely for lawful business purposes.</li>
    <li>Any damage beyond normal wear and tear will be assessed and charged to the lessee.</li>
    <li>Monthly payments are due on the 1st of each calendar month.</li>
    <li>Early termination requires 30 days written notice and may attract a penalty fee.</li>
    <li>On The Go Global reserves the right to substitute vehicles of equivalent specification.</li>
    <li>This agreement is governed by the laws of the Republic of Kenya.</li>
  </ul>

  <!-- Signatures -->
  <div class="signatures">
    <div class="sig-block">
      <div class="line"></div>
      <div class="name">${company?.contact_person || 'Authorised Signatory'}</div>
      <div class="role">${company?.company_name || 'Client'}</div>
      <div class="role" style="margin-top:4px;">Date: ___________________</div>
    </div>
    <div class="sig-block">
      <div class="line"></div>
      <div class="name">On The Go Global</div>
      <div class="role">Corporate Fleet Manager</div>
      <div class="role" style="margin-top:4px;">Date: ___________________</div>
    </div>
  </div>

  <div class="footer">
    On The Go Global · Corporate Fleet Leasing · Nairobi, Kenya · onthegoglobal.co.ke<br/>
    Contract Ref: ${contract_ref} · Generated: ${new Date(generated_at)?.toISOString()}
  </div>
</div>
</body>
</html>`;
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase?.rpc('generate_lease_contract', {
        p_account_id: accountId,
      });

      if (rpcError) throw rpcError;
      if (!data?.success) throw new Error(data?.error || 'Contract generation failed');

      const html = buildContractHTML(data);

      // Open in new window and trigger print-to-PDF
      const win = window.open('', '_blank', 'width=1000,height=800');
      if (!win) {
        throw new Error('Popup blocked. Please allow popups for this site and try again.');
      }
      win?.document?.write(html);
      win?.document?.close();
      win?.focus();
      setTimeout(() => {
        win?.print();
      }, 600);

      if (onClose) onClose();
    } catch (err) {
      setError(err?.message || 'Failed to generate contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="rounded-2xl w-full max-w-md shadow-2xl" style={{ background: '#0d1f3c', border: '1px solid rgba(255,215,0,0.2)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,215,0,0.15)' }}>
              <Icon name="FileText" size={20} style={{ color: '#ffd700' }} />
            </div>
            <div>
              <h3 className="text-white font-bold text-base">Generate Lease Contract</h3>
              <p className="text-white/40 text-xs mt-0.5">{accountName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <Icon name="X" size={18} className="text-white/50" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-white/70 text-sm font-medium">The generated PDF will include:</p>
            <ul className="space-y-1.5">
              {[
                { icon: 'Building2', text: 'Company & contact details' },
                { icon: 'Car', text: 'Full assigned vehicle list' },
                { icon: 'FileText', text: 'All lease contracts & status' },
                { icon: 'DollarSign', text: 'Pricing & payment terms' },
                { icon: 'Shield', text: 'Terms, conditions & SLA' },
                { icon: 'PenLine', text: 'Signature blocks' },
              ]?.map(item => (
                <li key={item?.icon} className="flex items-center gap-2 text-xs text-white/50">
                  <Icon name={item?.icon} size={13} style={{ color: '#ffd700' }} />
                  {item?.text}
                </li>
              ))}
            </ul>
          </div>

          {error && (
            <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}>
              {error}
            </div>
          )}

          <p className="text-white/30 text-xs">
            A print dialog will open. Select "Save as PDF" to download the contract.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-white/50 hover:text-white hover:bg-white/10 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
            style={{ background: loading ? 'rgba(255,215,0,0.3)' : 'rgba(255,215,0,0.2)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.4)' }}
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Icon name="Download" size={16} />
                Generate &amp; Download PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaseContractGenerator;
